import { useState, type ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { formatDate } from '@/lib/utils'
import { GRACKLE_COLORS } from '@/lib/theme'
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Building2,
  Send,
  Star,
  Plus,
  FileText,
  Pencil,
  Trash2,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { EditActionBar } from '@/components/EditActionBar'
import { Badge } from '@/components/ui/badge'
import { HistoryTimeline } from '@/components/HistoryTimeline'
import { ActivityTimeline } from '@/components/ActivityTimeline'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { AddressAutocomplete, type AddressDetails } from '@/components/ui/address-autocomplete'
import {
  useContact,
  useUpdateContact,
  useDeleteContact,
  useUsers,
  useAccounts,
  useNotes,
  useNoteSessionsByContact,
} from '@/hooks/use-api'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { ownerPickerOptions } from '@/lib/salesReps'
import { useToast } from '@/hooks/use-toast'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { useClickToEdit } from '@/hooks/use-click-to-edit'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { ClickToCallButton } from '@/components/ClickToCallButton'
import { ComposeEmailModal } from '@/components/ComposeEmailModal'
import { CreateOpportunityModal } from '@/components/CreateOpportunityModal'
import { CreateTaskModal } from '@/components/CreateTaskModal'
import { NoteSessionPanel } from '@/components/notes/NoteSessionPanel'
import { MergedNotesList } from '@/components/notes/MergedNotesList'
import { NoteDetailModal } from '@/components/NoteDetailModal'
import { EditNoteModal } from '@/components/EditNoteModal'
import { useUser } from '@/contexts/UserContext'
import { contactFieldPerm, contactRoleOf } from '@/lib/contactFieldPermissions'
import { MailchimpContactSection } from '@/components/MailchimpContactSection'
import { useDeleteNote, useDeleteNoteSession } from '@/hooks/use-api'
import type { Note } from '@/types'
import type { NoteSession } from '@/types/noteSession'
import { explainError } from '@/lib/explain-error'

export default function ContactDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [composeModalOpen, setComposeModalOpen] = useState(false)
  const [createOpportunityOpen, setCreateOpportunityOpen] = useState(false)
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false)
  const [notePanelOpen, setNotePanelOpen] = useState(false)
  const [existingSessionId, setExistingSessionId] = useState<string | undefined>(undefined)
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)
  const [noteDetailModalOpen, setNoteDetailModalOpen] = useState(false)
  const [editingNote, setEditingNote] = useState<Note | null>(null)
  const [editNoteModalOpen, setEditNoteModalOpen] = useState(false)
  const [editingSession, setEditingSession] = useState<NoteSession | null>(null)

  const { toast } = useToast()
  const { isAdmin, impersonatedUser, currentUser } = useUser()
  const deleteNoteMutation = useDeleteNote(id)
  const deleteSessionMutation = useDeleteNoteSession()
  // Only Admin/Super Admin can delete (matching Accounts); blocked while
  // impersonating a non-admin.
  const canDelete =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  const deleteContactMutation = useDeleteContact()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const handleDeleteConfirm = async () => {
    if (!contact) return
    try {
      await deleteContactMutation.mutateAsync(contact.id)
      const name =
        `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed Contact'
      toast({
        title: 'Contact deleted',
        description: `"${name}" has been deleted.`,
      })
      setDeleteDialogOpen(false)
      navigate('/contacts')
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: explainError(error, 'Could not delete the contact.'),
        variant: 'destructive',
      })
    }
  }
  const { data: contact, isLoading, error } = useContact(id!)
  useDocumentTitle(
    contact
      ? `${`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Contact'} | Contact`
      : undefined
  )
  useRecordBreadcrumb(
    contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : undefined
  )
  const updateContactMutation = useUpdateContact()
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = usersData?.data ?? []
  const { data: accountsData } = useAccounts({ limit: 10000 })
  const accounts = accountsData?.data ?? []
  const { data: contactNotesData } = useNotes({ parent_id: id! })
  const contactNotes = contactNotesData?.data ?? []
  const { data: noteSessions } = useNoteSessionsByContact(id!)
  const LEAD_SOURCES = usePicklistOptions('contact', 'lead_source')
  const MARKETING_STATUSES = usePicklistOptions('contact', 'marketing_status__c')
  // Role-based field permissions. When an Admin previews-as another user, honour
  // the impersonated role so the record renders as that user would see it.
  const contactRole = contactRoleOf(
    (impersonatedUser?.user_type ?? currentUser?.user_type) as string | undefined
  )

  // Unified inline edit (mirrors Lead/Account detail pages)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editForm, setEditForm] = useState<Record<string, any>>({})
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  // Notify the new owner by email when the owner changes (default on; backend only
  // sends when owner_id actually changes). Mirrors Lead/Account/Opp detail pages.
  const [notifyOwner, setNotifyOwner] = useState(true)
  const canChangeOwner =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))

  const handleEditClick = () => {
    if (!contact || impersonatedUser) return // read-only while impersonating
    const form: Record<string, any> = {
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      title: contact.title || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile_phone: contact.mobile_phone || contact.mobile || '',
      department: contact.department || '',
      account_id: contact.account_id || '',
      owner_id: contact.owner_id || '',
      lead_source: contact.lead_source || '',
      status__c: contact.status__c || contact.status || '',
      marketing_status__c: contact.marketing_status__c || '',
      marketing_recheck_date__c: contact.marketing_recheck_date__c || null,
      role__c: contact.role__c || '',
      linkedin__c: contact.linkedin__c || '',
      secondary_email__c: contact.secondary_email__c || '',
      assistant_name: contact.assistant_name || '',
      assistant_phone: contact.assistant_phone || '',
      mailing_street: contact.mailing_street || '',
      mailing_city: contact.mailing_city || '',
      mailing_state: contact.mailing_state || '',
      mailing_postal_code: contact.mailing_postal_code || '',
      mailing_country: contact.mailing_country || '',
      is_reference: contact.is_reference || false,
      reference_consent_date: contact.reference_consent_date?.split('T')[0] || '',
      reference_consent_notes: contact.reference_consent_notes || '',
      email_marketing_consent__c: contact.email_marketing_consent__c || false,
      email_marketing_consent_date__c: contact.email_marketing_consent_date__c?.split('T')[0] || '',
    }
    setEditForm(form)
    setFormErrors({})
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    setEditForm({})
    setFormErrors({})
  }

  // Click-to-edit: clicking any read-only field enters edit mode. Hook must be
  // called before any early returns to keep hook order stable.
  const { bindReadOnly } = useClickToEdit(isEditMode, () => handleEditClick())

  const handleFieldChange = (field: string, value: any) => {
    setEditForm((prev) => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {}
    // Only validate format/picklist for fields the user actually CHANGED. Otherwise a
    // pre-existing/legacy value (e.g. an old lead_source no longer in the picklist, or a
    // LinkedIn saved without https://) would block an unrelated edit such as updating a
    // phone number — with nothing obvious to fix.
    const changed = (k: string) =>
      ((editForm as Record<string, unknown>)[k] ?? '') !==
      ((contact as unknown as Record<string, unknown>)?.[k] ?? '')
    if (!editForm.last_name?.trim()) errs.last_name = 'Last name is required'
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (editForm.email && changed('email') && !emailRe.test(editForm.email))
      errs.email = 'Invalid email format'
    if (
      editForm.secondary_email__c &&
      changed('secondary_email__c') &&
      !emailRe.test(editForm.secondary_email__c)
    )
      errs.secondary_email__c = 'Invalid secondary email format'
    if (
      editForm.linkedin__c?.trim() &&
      changed('linkedin__c') &&
      !/^https?:\/\/.+/.test(editForm.linkedin__c)
    )
      errs.linkedin__c = 'LinkedIn must be a valid URL (http:// or https://)'
    if (
      editForm.lead_source &&
      changed('lead_source') &&
      LEAD_SOURCES.length &&
      !LEAD_SOURCES.includes(editForm.lead_source)
    )
      errs.lead_source = 'Invalid lead source'
    setFormErrors(errs)
    return errs
  }

  const handleSave = async () => {
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      toast({
        title: 'Validation Error',
        description: Object.values(errs).join(' · '),
        variant: 'destructive',
      })
      // Surface the first invalid field (scroll + focus) so it's clear what needs fixing.
      const firstField = Object.keys(errs)[0]
      const el = firstField ? document.getElementById(firstField) : null
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el?.focus?.()
      return
    }
    // The contact PUT does a full-row replace (static SET + extensions UPSERT) and
    // validates last_name, so send the COMPLETE field set. Fields the page doesn't
    // edit (primary_contact__c, holiday_card__c, lead_source_details__c) are carried
    // over from the loaded contact so they aren't wiped.
    const payload = {
      first_name: editForm.first_name,
      last_name: editForm.last_name,
      email: editForm.email,
      phone: editForm.phone,
      mobile_phone: editForm.mobile_phone,
      title: editForm.title,
      account_id: editForm.account_id || null,
      owner_id: editForm.owner_id || null,
      department: editForm.department,
      lead_source: editForm.lead_source,
      mailing_street: editForm.mailing_street,
      mailing_city: editForm.mailing_city,
      mailing_state: editForm.mailing_state,
      mailing_postal_code: editForm.mailing_postal_code,
      mailing_country: editForm.mailing_country,
      linkedin__c: editForm.linkedin__c,
      status__c: editForm.status__c,
      marketing_status__c: editForm.marketing_status__c || null,
      marketing_recheck_date__c: editForm.marketing_recheck_date__c || null,
      role__c: editForm.role__c,
      secondary_email__c: editForm.secondary_email__c,
      is_reference: editForm.is_reference,
      reference_consent_notes: editForm.reference_consent_notes,
      reference_consent_date: editForm.reference_consent_date || null,
      email_marketing_consent__c: editForm.email_marketing_consent__c,
      email_marketing_consent_date__c: editForm.email_marketing_consent_date__c || null,
      // Preserve page-hidden extension fields
      primary_contact__c: contact?.primary_contact__c ?? false,
      holiday_card__c: contact?.holiday_card__c ?? false,
      lead_source_details__c: contact?.lead_source_details__c ?? '',
      // Gate the new-owner notification; backend only emails on an actual owner change
      notify_owner: notifyOwner,
    }
    try {
      const result = await updateContactMutation.mutateAsync({ id: id!, data: payload as any })
      setIsEditMode(false)
      setFormErrors({})
      // Surface non-fatal side-effect warnings (e.g. a Marketing Status change
      // whose Mailchimp unsubscribe / BDR task did not complete).
      const warnings = (result as { warnings?: string[] })?.warnings
      if (warnings && warnings.length) {
        toast({
          title: 'Contact updated with warnings',
          description: warnings.join(' '),
          variant: 'destructive',
        })
      } else {
        toast({ title: 'Contact updated' })
      }
    } catch (error: any) {
      const d = error?.response?.data
      toast({
        title: 'Error',
        description:
          (Array.isArray(d?.details) && d.details.length ? d.details.join(', ') : d?.error) ||
          'Failed to update contact',
        variant: 'destructive',
      })
    }
  }

  // Inline field renderer: read mode = value + click-to-edit pencil; edit mode = input.
  const renderField = (
    label: string,
    field: string,
    displayValue: ReactNode,
    type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'owner' | 'account' = 'text',
    options?: string[]
  ) => {
    // Role-based field permission: 'none' hides the field (call sites also hide
    // the label); 'read' shows a static value with no edit affordance.
    const perm = contactFieldPerm(contactRole, field)
    if (perm === 'none') return null
    if (perm === 'read') {
      return <div className="text-sm mt-1 break-words">{displayValue || 'N/A'}</div>
    }
    if (isEditMode) {
      if (type === 'owner') {
        if (!canChangeOwner) {
          return <div className="text-sm mt-1 text-muted-foreground">{displayValue}</div>
        }
        return (
          <div>
            <Select
              value={(editForm.owner_id as string) || ''}
              onValueChange={(v) => handleFieldChange('owner_id', v)}
            >
              <SelectTrigger id={field} className="mt-1">
                <SelectValue placeholder="Select owner" />
              </SelectTrigger>
              <SelectContent>
                {ownerPickerOptions(users, editForm.owner_id).map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 mt-2">
              <Checkbox
                id="contact-notify_owner"
                checked={notifyOwner}
                onCheckedChange={(checked) => setNotifyOwner(checked === true)}
              />
              <Label htmlFor="contact-notify_owner" className="text-sm font-normal cursor-pointer">
                Notify new owner by email (only if owner changes)
              </Label>
            </div>
          </div>
        )
      }
      if (type === 'account') {
        return (
          <div className="mt-1">
            <RecordCombobox
              id={field}
              records={accounts}
              value={(editForm.account_id as string) || ''}
              onValueChange={(v) => handleFieldChange('account_id', v)}
              placeholder="Search accounts..."
              searchPlaceholder="Search accounts..."
              emptyMessage="No accounts found."
              displayField={(a: any) => a.name || ''}
              aria-label="Account"
            />
          </div>
        )
      }
      if (type === 'select') {
        return (
          <div>
            <Select
              value={(editForm[field] as string) || ''}
              onValueChange={(v) => handleFieldChange(field, v)}
            >
              <SelectTrigger
                id={field}
                className={`mt-1 ${formErrors[field] ? 'border-destructive' : ''}`}
                aria-invalid={!!formErrors[field]}
                aria-describedby={formErrors[field] ? `${field}-error` : undefined}
              >
                <SelectValue placeholder={`Select ${label}`} />
              </SelectTrigger>
              <SelectContent>
                {(options || []).map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formErrors[field] && (
              <p id={`${field}-error`} className="text-sm text-destructive mt-1" role="alert">
                {formErrors[field]}
              </p>
            )}
          </div>
        )
      }
      if (type === 'textarea') {
        return (
          <Textarea
            id={field}
            value={(editForm[field] as string) || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            className="mt-1"
            rows={2}
          />
        )
      }
      return (
        <div>
          <Input
            id={field}
            type={type}
            value={(editForm[field] as string) || ''}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            className={`mt-1 ${formErrors[field] ? 'border-destructive' : ''}`}
            aria-invalid={!!formErrors[field]}
            aria-describedby={formErrors[field] ? `${field}-error` : undefined}
          />
          {formErrors[field] && (
            <p id={`${field}-error`} className="text-sm text-destructive mt-1" role="alert">
              {formErrors[field]}
            </p>
          )}
        </div>
      )
    }
    const p = bindReadOnly(field, label)
    return (
      <div {...p} className={`text-sm flex items-center justify-between gap-2 ${p.className}`}>
        <span className="min-w-0 break-words whitespace-pre-wrap">{displayValue || 'N/A'}</span>
        <Pencil className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96" role="status" aria-live="polite">
        <div className="text-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="progressbar"
            aria-label="Loading contact details"
          ></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading contact details...</p>
        </div>
      </div>
    )
  }

  if (error || !contact) {
    return (
      <div className="flex flex-col items-center justify-center h-96" role="alert">
        <p className="text-lg font-semibold text-destructive">Error loading contact</p>
        <p className="text-sm text-muted-foreground mt-2">
          {explainError(error, 'Contact not found')}
        </p>
        <Button
          onClick={() => navigate('/contacts')}
          className="mt-4"
          aria-label="Return to contacts list"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Contacts
        </Button>
      </div>
    )
  }

  const fullName =
    `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed Contact'

  return (
    <div className="space-y-6" role="main" aria-label="Contact details">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/contacts')}
            aria-label="Back to contacts list"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <CategoryBadge type="contact" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">{fullName}</h1>
              {contact.is_reference && (
                <Badge
                  variant="default"
                  style={{
                    backgroundColor: GRACKLE_COLORS.pollen,
                    color: GRACKLE_COLORS.prussianBlue,
                  }}
                >
                  <Star className="h-3 w-3 mr-1" aria-hidden="true" />
                  Reference
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {contact.title || 'No title'}
              {contact.account_name && ` at ${contact.account_name}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="default"
            onClick={() => setNotePanelOpen(true)}
            disabled={!!impersonatedUser}
            aria-label={
              impersonatedUser ? 'Read-only while impersonating' : `Open call notes for ${fullName}`
            }
            title={impersonatedUser ? 'Read-only while impersonating' : undefined}
          >
            <FileText className="mr-2 h-4 w-4" aria-hidden="true" />
            Call Notes
          </Button>
          <Button
            variant="outline"
            onClick={() => setCreateOpportunityOpen(true)}
            disabled={!contact.account_id}
            aria-label={
              contact.account_id
                ? `Create opportunity for ${contact.account_name || 'account'}`
                : 'Contact must be linked to an account to create an opportunity'
            }
            title={!contact.account_id ? 'Contact must be linked to an account' : undefined}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New Opportunity
          </Button>
          <Button
            variant="outline"
            onClick={() => setComposeModalOpen(true)}
            disabled={!contact.email}
            aria-label={
              contact.email ? `Send email to ${contact.email}` : 'No email address available'
            }
          >
            <Send className="mr-2 h-4 w-4" aria-hidden="true" />
            Send Email
          </Button>
          {!isEditMode && (
            <Button
              variant="outline"
              onClick={handleEditClick}
              disabled={!!impersonatedUser}
              aria-label="Edit contact"
              title={impersonatedUser ? 'Read-only while impersonating' : undefined}
            >
              <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
          )}
          {!isEditMode && canDelete && (
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
              aria-label={`Delete contact ${`${contact.first_name || ''} ${contact.last_name || ''}`.trim()}`}
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Delete Contact confirmation (Admin/Super Admin only) */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent aria-describedby="delete-contact-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription id="delete-contact-description">
              Are you sure you want to delete "
              {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'this contact'}
              "? Related records (opportunities, tickets, activities) keep their history, but the
              contact will no longer appear anywhere in the CRM. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete operation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteContactMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label="Confirm delete contact"
            >
              {deleteContactMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Email</CardTitle>
            <Mail className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium truncate">
              {contact.email ? (
                <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                  {contact.email}
                </a>
              ) : (
                'N/A'
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Phone</CardTitle>
            <Phone className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium">
              {contact.phone ? (
                <ClickToCallButton
                  phoneNumber={contact.phone}
                  whoId={contact.id}
                  whoType="Contact"
                  whoName={`${contact.first_name} ${contact.last_name}`}
                  whatId={contact.account_id}
                  whatType="Account"
                  doNotCall={contact.do_not_call}
                />
              ) : (
                'N/A'
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Account</CardTitle>
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium truncate">
              {contact.account_id && contact.account_name ? (
                <Link
                  to={`/accounts/${contact.account_id}`}
                  className="text-primary hover:underline"
                >
                  {contact.account_name}
                </Link>
              ) : (
                'N/A'
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Owner</CardTitle>
            <User className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-sm font-medium truncate">{contact.owner_name || 'Unassigned'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
            <CardDescription>Personal and communication details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">First Name</Label>
                {renderField('First Name', 'first_name', contact.first_name)}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Last Name *</Label>
                {renderField('Last Name', 'last_name', contact.last_name)}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                {renderField('Email', 'email', contact.email, 'email')}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Phone</Label>
                {renderField('Phone', 'phone', contact.phone, 'tel')}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Mobile</Label>
                {renderField(
                  'Mobile',
                  'mobile_phone',
                  contact.mobile_phone || contact.mobile,
                  'tel'
                )}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Title</Label>
                {renderField('Title', 'title', contact.title)}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Department</Label>
                {renderField('Department', 'department', contact.department)}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Account</Label>
                {renderField('Account', 'account_id', contact.account_name || 'N/A', 'account')}
              </div>
              {contactFieldPerm(contactRole, 'owner_id') !== 'none' && (
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Owner</Label>
                  {renderField('Owner', 'owner_id', contact.owner_name || 'Unassigned', 'owner')}
                </div>
              )}
              {contactFieldPerm(contactRole, 'status__c') !== 'none' && (
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                  {renderField(
                    'Status',
                    'status__c',
                    contact.status__c || contact.status,
                    'select',
                    ['Active', 'Inactive']
                  )}
                </div>
              )}
              {contactFieldPerm(contactRole, 'marketing_status__c') !== 'none' && (
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Marketing Status
                  </Label>
                  {renderField(
                    'Marketing Status',
                    'marketing_status__c',
                    contact.marketing_status__c,
                    'select',
                    MARKETING_STATUSES
                  )}
                  {contact.marketing_status__c === 'Nurture' &&
                    contact.marketing_recheck_date__c && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Recheck due {formatDate(contact.marketing_recheck_date__c)}
                      </p>
                    )}
                </div>
              )}
              {contactFieldPerm(contactRole, 'role__c') !== 'none' && (
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Role</Label>
                  {renderField('Role', 'role__c', contact.role__c)}
                </div>
              )}
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Lead Source</Label>
                {renderField(
                  'Lead Source',
                  'lead_source',
                  contact.lead_source,
                  'select',
                  LEAD_SOURCES
                )}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">LinkedIn</Label>
                {renderField(
                  'LinkedIn',
                  'linkedin__c',
                  contact.linkedin__c ? (
                    <a
                      href={contact.linkedin__c}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {contact.linkedin__c}
                    </a>
                  ) : (
                    'N/A'
                  )
                )}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Secondary Email</Label>
                {renderField(
                  'Secondary Email',
                  'secondary_email__c',
                  contact.secondary_email__c,
                  'email'
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mailchimp: subscription state + soft bounces (read-only) and tags
            (editable by Marketing/Admin). */}
        <MailchimpContactSection
          contact={contact}
          canEditTags={
            (contactRole === 'Marketing' || contactRole === 'Admin') && !impersonatedUser
          }
        />

        {/* Notes */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Notes ({contactNotes.length + (noteSessions?.length ?? 0)})</CardTitle>
              <CardDescription>Call notes and quick notes for this contact</CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setExistingSessionId(undefined)
                setNotePanelOpen(true)
              }}
              disabled={!!impersonatedUser}
              aria-label="Add a new call note for this contact"
              title={impersonatedUser ? 'Read-only while impersonating' : undefined}
            >
              <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
              New Note
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <MergedNotesList
              entityType="Contact"
              entityId={id!}
              legacyNotes={contactNotes}
              currentUserId={currentUser?.id}
              currentUserType={currentUser?.user_type}
              onOpenSession={(sessionId) => {
                setExistingSessionId(sessionId)
                setNotePanelOpen(true)
              }}
              onOpenLegacyNote={(note) => {
                setSelectedNoteId(note.id)
                setNoteDetailModalOpen(true)
              }}
              onEditNote={(note) => {
                setEditingNote(note)
                setEditNoteModalOpen(true)
              }}
              onDeleteNote={(note) => {
                deleteNoteMutation.mutate(note.id)
              }}
              onEditSession={(session) => {
                setEditingSession(session)
                setExistingSessionId(session.id)
                setNotePanelOpen(true)
              }}
              onDeleteSession={(session) => {
                deleteSessionMutation.mutate(session.id)
              }}
            />
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
            <CardDescription>Other contact details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Birth Date</Label>
                <div className="text-sm">
                  {contact.birth_date ? formatDate(contact.birth_date) : 'N/A'}
                </div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Assistant Name</Label>
                <div className="text-sm">{contact.assistant_name || 'N/A'}</div>
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Assistant Phone</Label>
                <div className="text-sm">{contact.assistant_phone || 'N/A'}</div>
              </div>
              <div className="py-2 border-b border-border">
                {isEditMode ? (
                  <AddressAutocomplete
                    id="mailing_street"
                    label="Mailing Street"
                    value={(editForm.mailing_street as string) || ''}
                    onStreetChange={(street) => handleFieldChange('mailing_street', street)}
                    onAddressSelect={(a: AddressDetails) =>
                      setEditForm((prev) => ({
                        ...prev,
                        mailing_street: a.street || '',
                        mailing_city: a.city || '',
                        mailing_state: a.state || a.state_code || '',
                        mailing_postal_code: a.postal_code || '',
                        mailing_country: a.country || '',
                      }))
                    }
                    placeholder="Start typing address..."
                  />
                ) : (
                  <>
                    <Label className="text-sm font-medium text-muted-foreground">
                      Mailing Street
                    </Label>
                    {renderField('Mailing Street', 'mailing_street', contact.mailing_street)}
                  </>
                )}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Mailing City</Label>
                {renderField('Mailing City', 'mailing_city', contact.mailing_city)}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Mailing State</Label>
                {renderField('Mailing State', 'mailing_state', contact.mailing_state)}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Postal Code</Label>
                {renderField('Postal Code', 'mailing_postal_code', contact.mailing_postal_code)}
              </div>
              <div className="py-2 border-b border-border">
                <Label className="text-sm font-medium text-muted-foreground">Country</Label>
                {renderField('Country', 'mailing_country', contact.mailing_country)}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reference Information - row 2, beside Additional Information */}
        <Card>
          <CardHeader>
            <CardTitle>Reference Information</CardTitle>
            <CardDescription>Track reference status and consent details</CardDescription>
          </CardHeader>
          <CardContent>
            {isEditMode ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_reference"
                    checked={!!editForm.is_reference}
                    onChange={(e) => handleFieldChange('is_reference', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                    aria-label="Mark as reference"
                  />
                  <Label htmlFor="is_reference" className="text-sm font-medium">
                    This contact is a reference
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="email_marketing_consent"
                    checked={!!editForm.email_marketing_consent__c}
                    onChange={(e) =>
                      handleFieldChange('email_marketing_consent__c', e.target.checked)
                    }
                    className="h-4 w-4 rounded border-gray-300"
                    aria-label="Consent to receive marketing email"
                  />
                  <Label htmlFor="email_marketing_consent" className="text-sm font-medium">
                    Consent to receive marketing email (Mailchimp: subscribed vs transactional)
                  </Label>
                </div>
                {!!editForm.email_marketing_consent__c && (
                  <div>
                    <Label
                      htmlFor="email_marketing_consent_date"
                      className="text-sm font-medium text-muted-foreground"
                    >
                      Marketing Consent Date
                    </Label>
                    <DatePicker
                      id="email_marketing_consent_date"
                      value={editForm.email_marketing_consent_date__c as string}
                      onChange={(v) =>
                        handleFieldChange('email_marketing_consent_date__c', v ?? '')
                      }
                      className="mt-1 max-w-xs"
                      aria-label="Marketing consent date"
                    />
                  </div>
                )}
                <div>
                  <Label
                    htmlFor="reference_consent_date"
                    className="text-sm font-medium text-muted-foreground"
                  >
                    Consent Date
                  </Label>
                  <DatePicker
                    id="reference_consent_date"
                    value={editForm.reference_consent_date as string}
                    onChange={(v) => handleFieldChange('reference_consent_date', v ?? '')}
                    className="mt-1 max-w-xs"
                    aria-label="Reference consent date"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="reference_consent_notes"
                    className="text-sm font-medium text-muted-foreground"
                  >
                    Consent Notes
                  </Label>
                  <Textarea
                    id="reference_consent_notes"
                    value={(editForm.reference_consent_notes as string) || ''}
                    onChange={(e) => handleFieldChange('reference_consent_notes', e.target.value)}
                    placeholder="Notes about when and how consent was given..."
                    className="mt-1"
                    rows={3}
                    aria-label="Reference consent notes"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">
                    Reference Status
                  </Label>
                  {renderField(
                    'Reference Status',
                    'is_reference',
                    contact.is_reference ? (
                      <Badge
                        variant="default"
                        style={{
                          backgroundColor: GRACKLE_COLORS.pollen,
                          color: GRACKLE_COLORS.prussianBlue,
                        }}
                      >
                        <Star className="h-3 w-3 mr-1" aria-hidden="true" />
                        Active Reference
                      </Badge>
                    ) : (
                      'Not a reference'
                    )
                  )}
                </div>
                <div className="py-2 border-b border-border">
                  <Label className="text-sm font-medium text-muted-foreground">Consent Date</Label>
                  {renderField(
                    'Consent Date',
                    'reference_consent_date',
                    contact.reference_consent_date
                      ? formatDate(contact.reference_consent_date)
                      : 'N/A'
                  )}
                </div>
                <div className="py-2 border-b border-border md:col-span-2">
                  <Label className="text-sm font-medium text-muted-foreground">Consent Notes</Label>
                  {renderField(
                    'Consent Notes',
                    'reference_consent_notes',
                    contact.reference_consent_notes || 'N/A'
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity + Contact History side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Timeline */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Activity</CardTitle>
              <CardDescription>Tasks, events, and emails related to this contact</CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCreateTaskModalOpen(true)}
              disabled={!!impersonatedUser}
              aria-label="Create new task for this contact"
              title={impersonatedUser ? 'Read-only while impersonating' : undefined}
            >
              <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
              New Task
            </Button>
          </CardHeader>
          <CardContent>
            <ActivityTimeline entityId={id!} entityType="Contact" />
          </CardContent>
        </Card>

        {/* Contact History */}
        <Card>
          <CardHeader>
            <CardTitle>Contact History</CardTitle>
            <CardDescription>Track changes made to this contact</CardDescription>
          </CardHeader>
          <CardContent>
            <HistoryTimeline entityId={id!} entityType="Contact" />
          </CardContent>
        </Card>
      </div>

      <EditActionBar
        isEditMode={isEditMode}
        onSave={handleSave}
        onCancel={handleCancelEdit}
        isSaving={updateContactMutation.isPending}
      />

      {/* Compose Email Modal */}
      <ComposeEmailModal
        open={composeModalOpen}
        onOpenChange={setComposeModalOpen}
        defaultTo={contact.email}
      />

      {/* Create Opportunity Modal */}
      <CreateOpportunityModal
        open={createOpportunityOpen}
        onOpenChange={setCreateOpportunityOpen}
        accountId={contact.account_id || undefined}
        accountName={contact.account_name || ''}
        contactId={contact.id}
      />

      {/* Create Task Modal */}
      <CreateTaskModal
        open={createTaskModalOpen}
        onOpenChange={setCreateTaskModalOpen}
        relatedToId={contact.id}
        relatedToType="Contact"
        relatedToName={fullName}
        ownerId={contact.owner_id}
      />

      {/* Note Session Panel */}
      <NoteSessionPanel
        entityType="Contact"
        entityId={contact.id}
        entityName={fullName}
        open={notePanelOpen}
        onOpenChange={(open) => {
          setNotePanelOpen(open)
          if (!open) {
            setExistingSessionId(undefined)
            setEditingSession(null)
          }
        }}
        readOnly={!!impersonatedUser}
        existingSessionId={existingSessionId}
        allowEdit={editingSession?.id === existingSessionId}
      />

      {/* Note Detail Modal (read-only view of a legacy/quick note) */}
      <NoteDetailModal
        noteId={selectedNoteId}
        open={noteDetailModalOpen}
        onOpenChange={setNoteDetailModalOpen}
        currentUserId={currentUser?.id}
        currentUserType={currentUser?.user_type}
        onEdit={(note) => {
          setSelectedNoteId(null)
          setNoteDetailModalOpen(false)
          setEditingNote(note)
          setEditNoteModalOpen(true)
        }}
        onDelete={(note) => {
          deleteNoteMutation.mutate(note.id)
          setNoteDetailModalOpen(false)
        }}
      />

      {/* Edit Note Modal */}
      <EditNoteModal
        open={editNoteModalOpen}
        onOpenChange={setEditNoteModalOpen}
        note={editingNote}
        parentId={id!}
      />
    </div>
  )
}
